import { CdkMenuModule } from '@angular/cdk/menu';
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirContentComponent } from '../content/content.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirTextComponent } from '../text/text.component';
import type { PalantirCursorBasePaginationService } from './cursor-pagination.type';

@Component({
  selector: 'palantir-cursor-pagination',
  templateUrl: './cursor-pagination.component.html',
  styleUrls: ['./cursor-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CdkMenuModule,
    FormsModule,
    PalantirTextComponent,
    PalantirButtonComponent,
    PalantirIconComponent,
    PalantirContentComponent,
  ],
})
export class PalantirCursorPaginationComponent<T extends PalantirCursorBasePaginationService> {
  @Input({ required: true }) public service!: T;

  @Input({ required: true }) public perPageLabel!: string;

  public paginationOptions = signal<number[]>([10, 25, 50, 100]);

  public async changePaginationSize(size: number): Promise<void> {
    this.service.take.update(() => size);
    this.service.cursor.update(() => null);
    this.service.search$.next();
  }

  public async nextPage(): Promise<void> {
    if (
      !this.service.pageInfo()?.nextNode ||
      this.service.isLoading() ||
      this.service.isSearching?.()
    ) {
      return;
    }

    this.service.cursor.set(this.service.pageInfo().nextNode);
    this.service.search$.next();
  }

  public async previousPage(): Promise<void> {
    if (
      !this.service.pageInfo()?.previousNode ||
      this.service.isLoading() ||
      this.service.isSearching?.()
    ) {
      return;
    }

    this.service.cursor.update(() => this.service.pageInfo().previousNode);
    this.service.search$.next();
  }

  public async firstPage(): Promise<void> {
    if (
      !this.service.pageInfo()?.previousNode ||
      this.service.isLoading() ||
      this.service.isSearching?.()
    ) {
      return;
    }

    this.service.cursor.update(() => null);
    this.service.search$.next();
  }

  public trackByFn(index: number): number {
    return index;
  }
}
